import React, { useCallback, useMemo } from "react";
import { Disclosure } from "@headlessui/react";
import { ChevronDownIcon } from "@heroicons/react/solid";
import { Field, useFormikContext } from "formik";
import UploadChangedDocuments from "./FileUploadForm";
import NotApprovedChangedDocuments from "./NotApprovedDocuments";
import { getChangedDocumentsModuleData } from "config/changesDocumentData";

const documentConfig = {
  "Director Approval": [
    {
      key: "directorResolution",
      title: "Director Resolution",
      fieldName: "directorResolution",
    },
    {
      key: "statutoryDeclaration",
      title: "Statutory Declaration",
      fieldName: "statutoryDeclaration",
    },
  ],
  "Shareholder Approval": [
    {
      key: "shareholderResolution",
      title: "Shareholder Resolution",
      fieldName: "shareholderResolution",
    },
  ],
  "Gazette Notice": [
    {
      key: "gazetteNotice",
      title: "Gazette Notice",
      fieldName: "gazetteNotice",
    },
  ],
  "Creditors Resolution": [
    {
      key: "creditorsResolution",
      title: "Creditors Resolution",
      fieldName: "creditorsResolution",
    },
  ],
  "Court Order": [
    { key: "courtOrder", title: "Court Order", fieldName: "courtOrder" },
  ],
  "appointment-letter": [
    {
      key: "appointmentLetter",
      title: "Notice of Appointment Letter",
      fieldName: "appointmentLetter",
    },
  ],
  "appointing-official": [
    {
      key: "approvedChanges",
      title: "Consent to Act Document",
      fieldName: "approvedChanges",
    },
  ],
  approvedChangesDocuments: [
    {
      key: "approvedChanges",
      title: "Consent to Act Document",
      fieldName: "approvedChanges",
    },
  ],
};

const ChangedDocuments = React.memo(
  ({
    uploadRequestType = "",
    field,
    form,
    documentsFor = "",
    openKey,
    setOpenKey = null,
    idType,
    designation,
    isInUpdateCompanyDetailsContext = false,
  }) => {
    const { setFieldValue } = useFormikContext();

    const documents = useMemo(
      () =>
        documentConfig[uploadRequestType] ||
        documentConfig.approvedChangesDocuments,
      [uploadRequestType]
    );

    const signedDocumentKey = useMemo(() => {
      if (uploadRequestType === "Director Approval")
        return "directorApprovalSignedDocuments";
      if (uploadRequestType === "Shareholder Approval")
        return "shareholderApprovalSignedDocuments";
      if (uploadRequestType === "Gazette Notice")
        return "gazetteApprovalSignedDocuments";
      if (uploadRequestType === "Creditors Resolution")
        return "creditorsApprovalSignedDocuments";
      if (uploadRequestType === "Court Order")
        return "courtOrderApprovalSignedDocuments";
      if (uploadRequestType === "appointment-letter")
        return "appointmentLetter";
      if (uploadRequestType === "appointing-official")
        return "appointingOfficialSignedDocuments";
      return "approvedChangesDocuments";
    }, [uploadRequestType]);

    const notApprovedFieldName = useMemo(() => {
      if (uploadRequestType === "Director Approval")
        return "directorResolution";
      if (uploadRequestType === "Shareholder Approval")
        return "shareholderResolution";
      if (uploadRequestType === "appointment-letter")
        return "appointmentResolutionType";
      return "consentResolutionType";
    }, [uploadRequestType]);

    const documentsAreSigned = form.values[field.name]?.documentsAreSigned;

    const handleToggle = useCallback(
      (key) => {
        if (!setOpenKey) return;
        setOpenKey((prevKey) => (prevKey === key ? null : key));
      },
      [setOpenKey]
    );

    const handleSetValue = useCallback(
      (e) => {
        const selectedValue = e.target.value;
        setFieldValue(field.name, {
          ...form.values[field.name],
          [e.target.name]: selectedValue,
        });
      },
      [setFieldValue, field.name, form.values]
    );

    const changeForms = (
      <div className="flex flex-col">
        {!isInUpdateCompanyDetailsContext && (
          <div className="grid grid-flow-col justify-stretch mt-4 mb-2">
            <div className="flex items-center">
              <label
                htmlFor={`consentResolutionType-${field.name}`}
                className="flex items-center cursor-pointer"
              >
                <Field
                  id={`consentResolutionType-${field.name}`}
                  name="documentsAreSigned"
                  type="radio"
                  value="true"
                  checked={documentsAreSigned === "true"}
                  onChange={handleSetValue}
                  className="cursor-pointer form-radio h-4 w-4 border-green-900 border-2 focus:ring-green-50"
                />
                <span className="ml-2">I have signed documents</span>
              </label>
            </div>

            <div className="flex items-center">
              <label
                htmlFor={`consentResolutionType-not-${field.name}`}
                className="flex items-center cursor-pointer"
              >
                <Field
                  id={`consentResolutionType-not-${field.name}`}
                  name="documentsAreSigned"
                  type="radio"
                  value="false"
                  checked={documentsAreSigned === "false"}
                  onChange={handleSetValue}
                  className="cursor-pointer form-radio h-4 w-4 border-green-900 border-2 focus:ring-green-50"
                />
                <span className="ml-2">Not yet signed</span>
              </label>
            </div>
          </div>
        )}

        {(documentsAreSigned === "true" || isInUpdateCompanyDetailsContext) && (
          <div className="container bg-white mt-4 px-4 py-4 shadow-sm rounded-md">
            <Field
              name="fileUploads"
              component={UploadChangedDocuments}
              documentData={getChangedDocumentsModuleData(signedDocumentKey)}
              disabled={false}
              idType={idType}
              designation={designation}
            />
          </div>
        )}

        {documentsAreSigned === "false" &&
          uploadRequestType !== "Gazette Notice" &&
          uploadRequestType !== "Court Order" &&
          uploadRequestType !== "Creditors Resolution" && (
            <div className="mt-4">
              <Field
                name={notApprovedFieldName}
                component={NotApprovedChangedDocuments}
                uploadRequestType={uploadRequestType}
                documentsFor={documentsFor}
                idType={idType}
                designation={designation}
              />
            </div>
          )}
      </div>
    );

    const renderAccordion = ({ key, title }) => (
      <div className="rounded-lg" key={key}>
        <Disclosure defaultOpen={openKey === key}>
          <>
            <Disclosure.Button
              className="flex w-full justify-between px-4 py-3 text-left bg-gray-20 hover:bg-gray-50 rounded-lg"
              onClick={() => handleToggle(key)}
            >
              <span className="font-medium text-gray-900">{title}</span>
              <ChevronDownIcon
                className={`${
                  openKey === key ? "rotate-180 transform" : ""
                } h-5 w-5 text-gray-500`}
              />
            </Disclosure.Button>
            {openKey === key && (
              <Disclosure.Panel
                static
                className="px-4 py-3 border-t border-gray-100 bg-white rounded-b-lg"
              >
                {changeForms}
              </Disclosure.Panel>
            )}
          </>
        </Disclosure>
      </div>
    );

    if (setOpenKey) {
      return (
        <div className="space-y-4 mt-4">{documents.map(renderAccordion)}</div>
      );
    }

    return changeForms;
  }
);

export default ChangedDocuments;
